// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * TrustedPersonService - MVP version
 * Stores trusted person config in Dexie.
 * Sleutelcode is displayed once and never stored plaintext.
 * In MVP: codeHash stored as a simple SHA-256 (no bcrypt dependency needed).
 * DOB hash: PBKDF2 same as profile service.
 */
import { db }                   from '@/lib/db'
import { generateUUID, nowISO } from '@/lib/utils'
import { getMasterKey }         from '@/services/profile'
import {
  SyncStatus,
  TrustedPersonRole,
  TrustedPersonRelationship,
  ActivationTrigger,
  ActivationStatus,
  ConfirmationStatus,
  ModulePermission,
} from '@/types'

// ── Code generation ───────────────────────────────────────────────────────────
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789' // 31 chars, no O/I/L/0/1
const MAX_VALID = Math.floor(256 / ALPHABET.length) * ALPHABET.length

export function generateAccessCode(): string {
  const result: string[] = []
  while (result.length < 8) {
    const byte = crypto.getRandomValues(new Uint8Array(1))[0]
    if (byte < MAX_VALID) result.push(ALPHABET[byte % ALPHABET.length])
  }
  return result.join('')
}

export function formatCodeDisplay(raw: string): string {
  return `${raw.slice(0, 4)}-${raw.slice(4)}`
}

export function normalizeCode(input: string): string {
  return input.replace(/[-–\s]/g, '').toUpperCase().trim()
}

// ── Hashing ───────────────────────────────────────────────────────────────────
async function sha256Hex(input: string): Promise<string> {
  const buf    = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

async function pbkdf2Hex(input: string, salt: string): Promise<string> {
  const enc = new TextEncoder()
  const km  = await crypto.subtle.importKey('raw', enc.encode(input), 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: enc.encode(salt), iterations: 310_000 },
    km, 256,
  )
  return Array.from(new Uint8Array(bits)).map(b => b.toString(16).padStart(2, '0')).join('')
}

// ── Persoonsdetails-blob (V49-UAT) ──────────────────────────────────────────────
// Naam/relatie blijven in plaintext kolommen (firstName/relationship) en bepalen
// de toegangslogica NIET. De extra persoonsgegevens van de vertrouwenspersoon
// (achternaam, geboortedatum, telefoon, e-mail) worden versleuteld opgeslagen in
// het reeds bestaande encryptedData/blobIv-slot. Dit is GEEN eigenaar-geboortedatum:
// de eigenaar-DOB voor toegang/verificatie loopt via dobHash en blijft ongemoeid.
export interface TrustedPersonDetails {
  lastName?: string
  dob?:      string    // geboortedatum van de VP zelf (niet de eigenaar)
  phone?:    string
  email?:    string
}

async function encryptBlob(data: object, key: CryptoKey): Promise<{ blob: ArrayBuffer; iv: string }> {
  const iv      = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(JSON.stringify(data))
  const blob    = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded)
  return { blob, iv: btoa(String.fromCharCode(...iv)) }
}

async function decryptBlob<T>(blob: ArrayBuffer, ivB64: string, key: CryptoKey): Promise<T> {
  const iv        = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0))
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, blob)
  return JSON.parse(new TextDecoder().decode(decrypted)) as T
}

function toPlainBlob(data: object): ArrayBuffer {
  return new TextEncoder().encode(JSON.stringify(data)).buffer as ArrayBuffer
}
function fromPlainBlob<T>(buf: ArrayBuffer): T {
  return JSON.parse(new TextDecoder().decode(buf)) as T
}

/** Bevat het detail-object minstens één niet-lege waarde? */
function hasAnyDetail(d?: TrustedPersonDetails): boolean {
  return !!d && Object.values(d).some(v => typeof v === 'string' && v.trim() !== '')
}

/** Versleutel (of plain-fallback) persoonsdetails voor opslag. */
async function encodeDetails(
  details: TrustedPersonDetails, userId: string,
): Promise<{ encryptedData: ArrayBuffer; blobIv: string }> {
  const key = getMasterKey(userId)
  if (key) {
    const { blob, iv } = await encryptBlob(details, key)
    return { encryptedData: blob, blobIv: iv }
  }
  return { encryptedData: toPlainBlob(details), blobIv: '' }
}

/** Lees persoonsdetails van een vertrouwenspersoon (of null als er niets is). */
export async function loadTrustedPersonDetails(
  personId: string, userId: string,
): Promise<TrustedPersonDetails | null> {
  const p = await db.trusted_persons.get(personId)
  if (!p || !p.encryptedData) return null            // oude records: encryptedData === null → geen details
  if (p.blobIv) {
    const key = getMasterKey(userId)
    if (!key) return null                            // key weg na reload → null, geen overschrijving
    try { return await decryptBlob<TrustedPersonDetails>(p.encryptedData, p.blobIv, key) }
    catch { return null }
  }
  try { return fromPlainBlob<TrustedPersonDetails>(p.encryptedData) }
  catch { return null }
}

// ── Public types ──────────────────────────────────────────────────────────────
export interface TrustedPersonConfig {
  id:                  string
  firstName:           string
  relationship:        string
  email:               string
  activationTrigger:   string
  accessKeyId:         string
  confirmationStatus:  string
  role:                'PRIMARY' | 'RESERVE'
  // Permissions
  moduleFuneralWishes:    boolean
  modulePersonalFarewell: boolean
  moduleMedicalWishes:    boolean
  moduleDocumentIndex:    boolean
  moduleMessages:         boolean
  moduleDigitalEstate:    boolean
  moduleLifeStory:        boolean
}

// ── Load ──────────────────────────────────────────────────────────────────────
export async function loadTrustedPersons(userId: string): Promise<TrustedPersonConfig[]> {
  const persons = await db.trusted_persons.where('userId').equals(userId).toArray()
  const result: TrustedPersonConfig[] = []

  for (const p of persons) {
    const key  = p.accessKeyId ? await db.access_keys.get(p.accessKeyId)   : null
    const perm = key            ? await db.key_permissions.where('accessKeyId').equals(key.id).first() : null
    result.push({
      id:                     p.id,
      firstName:              p.firstName,
      relationship:           p.relationship,
      email:                  '',   // encrypted - not loaded in list view
      activationTrigger:      key?.activationTrigger ?? ActivationTrigger.ON_DEATH,
      accessKeyId:            p.accessKeyId,
      confirmationStatus:     p.confirmationStatus,
      role:                   p.role,
      moduleFuneralWishes:    perm?.moduleFuneralWishes    === ModulePermission.VISIBLE,
      modulePersonalFarewell: perm?.modulePersonalFarewell === ModulePermission.VISIBLE,
      moduleMedicalWishes:    perm?.moduleMedicalWishes    === ModulePermission.VISIBLE,
      moduleDocumentIndex:    perm?.moduleDocumentIndex    === ModulePermission.VISIBLE,
      moduleMessages:         perm?.moduleMessages         === ModulePermission.VISIBLE,
      moduleDigitalEstate:    perm?.moduleDigitalEstate    === ModulePermission.VISIBLE,
      moduleLifeStory:        perm?.moduleLifeStory        === ModulePermission.VISIBLE,
    })
  }
  return result
}

// ── Create ────────────────────────────────────────────────────────────────────
export interface CreateTrustedPersonParams {
  userId:            string
  dateOfBirth:       string    // of de eigenaar - voor dobHash
  firstName:         string
  relationship:      TrustedPersonRelationship
  email:             string
  activationTrigger: ActivationTrigger
  role:              TrustedPersonRole
  permissions: {
    moduleFuneralWishes:    boolean
    modulePersonalFarewell: boolean
    moduleMedicalWishes:    boolean
    moduleDocumentIndex:    boolean
    moduleMessages:         boolean
    moduleDigitalEstate:    boolean
    moduleLifeStory:        boolean
  }
  /** Optionele persoonsgegevens van de VP zelf (worden versleuteld opgeslagen). */
  personDetails?:    TrustedPersonDetails
}

export interface CreateTrustedPersonResult {
  personId:    string
  accessKeyId: string
  rawCode:     string       // displayed ONCE, then discarded
  displayCode: string       // formatted XXXX-XXXX
}

export async function createTrustedPerson(
  params: CreateTrustedPersonParams,
): Promise<CreateTrustedPersonResult> {
  const rawCode    = generateAccessCode()
  const codeHash   = await sha256Hex(normalizeCode(rawCode) + params.userId)
  const dobHash    = await pbkdf2Hex(params.dateOfBirth, params.userId)

  const keyId    = generateUUID()
  const personId = generateUUID()
  const permId   = generateUUID()

  // V49-UAT: optionele persoonsdetails versleuteld in het bestaande slot.
  // E-mail uit params blijft backward compatible meegenomen.
  const detailsInput: TrustedPersonDetails = {
    ...(params.personDetails ?? {}),
    email: params.personDetails?.email ?? params.email,
  }
  const detailBlob = hasAnyDetail(detailsInput)
    ? await encodeDetails(detailsInput, params.userId)
    : null

  function perm(enabled: boolean): ModulePermission {
    return enabled ? ModulePermission.VISIBLE : ModulePermission.HIDDEN
  }

  await db.access_keys.add({
    id:                  keyId,
    userId:              params.userId,
    trustedPersonId:     personId,
    keyType:             params.role,
    codeHash,
    dobHash,
    activationTrigger:   params.activationTrigger,
    activationStatus:    ActivationStatus.INACTIVE,
    accessStartDate:     null,
    accessEndDate:       null,
    accessDurationDays:  null,
    inactivityEnabled:   false,
    inactivityDays:      null,
    inactivityStartedAt: null,
    generatedAt:         nowISO(),
    lastRotatedAt:       null,
    rotationCount:       0,
    invalidatedAt:       null,
    cloudSynced:         false,
    syncStatus:          SyncStatus.PENDING,
  })

  await db.key_permissions.add({
    id:                     permId,
    accessKeyId:            keyId,
    userId:                 params.userId,
    moduleEmergencyCard:    ModulePermission.ALWAYS,
    moduleFuneralWishes:    perm(params.permissions.moduleFuneralWishes),
    modulePersonalFarewell: perm(params.permissions.modulePersonalFarewell),
    moduleMedicalWishes:    perm(params.permissions.moduleMedicalWishes),
    moduleDocumentIndex:    perm(params.permissions.moduleDocumentIndex),
    moduleDigitalEstate:    perm(params.permissions.moduleDigitalEstate),
    moduleMessages:         perm(params.permissions.moduleMessages),
    moduleLifeStory:        perm(params.permissions.moduleLifeStory),
    setByUser:              true,
    createdAt:              nowISO(),
    updatedAt:              nowISO(),
    syncStatus:             SyncStatus.PENDING,
  })

  await db.trusted_persons.add({
    id:                  personId,
    userId:              params.userId,
    role:                params.role,
    firstName:           params.firstName,
    relationship:        params.relationship,
    accessKeyId:         keyId,
    confirmationStatus:  ConfirmationStatus.PENDING,
    confirmedAt:         null,
    createdAt:           nowISO(),
    updatedAt:           nowISO(),
    revokedAt:           null,
    syncStatus:          SyncStatus.PENDING,
    cloudSyncId:         null,
    encryptedData:       detailBlob ? detailBlob.encryptedData : null,
    blobIv:              detailBlob ? detailBlob.blobIv : null,
  })

  return { personId, accessKeyId: keyId, rawCode, displayCode: formatCodeDisplay(rawCode) }
}

// ── Revoke ────────────────────────────────────────────────────────────────────
export async function revokeTrustedPerson(personId: string): Promise<void> {
  const person = await db.trusted_persons.get(personId)
  if (!person) return
  await db.trusted_persons.update(personId, { revokedAt: nowISO(), confirmationStatus: ConfirmationStatus.REVOKED })
  if (person.accessKeyId) {
    await db.access_keys.update(person.accessKeyId, {
      activationStatus: ActivationStatus.REVOKED,
      invalidatedAt:    nowISO(),
    })
  }
}

// ── Update permissions ────────────────────────────────────────────────────────
export async function updatePermissions(
  accessKeyId: string,
  perms: Partial<{
    moduleFuneralWishes: boolean; modulePersonalFarewell: boolean
    moduleMedicalWishes: boolean; moduleDocumentIndex:    boolean
    moduleMessages:      boolean; moduleDigitalEstate:    boolean
    moduleLifeStory:     boolean
  }>,
): Promise<void> {
  const existing = await db.key_permissions.where('accessKeyId').equals(accessKeyId).first()
  if (!existing) return
  function p(v: boolean | undefined, current: ModulePermission) {
    if (v === undefined) return current
    return v ? ModulePermission.VISIBLE : ModulePermission.HIDDEN
  }
  await db.key_permissions.update(existing.id, {
    moduleFuneralWishes:    p(perms.moduleFuneralWishes,    existing.moduleFuneralWishes),
    modulePersonalFarewell: p(perms.modulePersonalFarewell, existing.modulePersonalFarewell),
    moduleMedicalWishes:    p(perms.moduleMedicalWishes,    existing.moduleMedicalWishes),
    moduleDocumentIndex:    p(perms.moduleDocumentIndex,    existing.moduleDocumentIndex),
    moduleMessages:         p(perms.moduleMessages,         existing.moduleMessages),
    moduleDigitalEstate:    p(perms.moduleDigitalEstate,    existing.moduleDigitalEstate),
    moduleLifeStory:        p(perms.moduleLifeStory,        existing.moduleLifeStory),
    setByUser:              true,
    updatedAt:              nowISO(),
  })
}
